<template>
  <div class="tw-w-full tw-relative">
    <o-autocomplete
      ref="searchAutocomplete"
      v-model="query"
      placeholder="Search Ticker Symbol"
      open-on-focus
      :data="searchResults"
      field="display"
      @select="handleSelect"
      @keydown.native.enter="selectFirstResult"
    >
      <template slot-scope="{ option }">
        <div class="media-content tw-whitespace-normal">
          <strong>{{ option.ticker.symbol }}</strong>
          <br />
          <span class="tw-text-sm tw-text-gray-600">{{ option.ticker.full_name }}</span>
        </div>
      </template>
    </o-autocomplete>
    <div class="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-flex-col justify-center">
      <i :class="['fas', 'fa-search', 'tw-px-3']" />
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'SearchTickers',
  props: ['initValue'],
  data() {
    return {
      query: '',
      selected: null,
      exactResults: [],
      fuzzyResults: [],
      searchResults: [],
      fuzzyAbortController: null,
    };
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
  },
  watch: {
    query(newQuery) {
      if (!newQuery || !newQuery.length) {
        this.searchResults = [];
        this.exactResults = [];
        this.fuzzyResults = [];
        return;
      }
      // Fire the exact search immediately.
      this.searchExact(newQuery);
      // Fire off the fuzzy search with debounce.
      this.debouncedFuzzySearch(newQuery);
    },
  },
  created() {
    // Debounce fuzzy search to prevent too many requests while typing.
    this.debouncedFuzzySearch = _.debounce(this.searchFuzzy, 500);
    this.findInitValue();
  },
  methods: {
    async findInitValue() {
      if (!this.initValue) return;
      this.query = this.initValue;
      const exactRes = await this.searchExact(this.initValue);
      if (exactRes && exactRes.length > 0) {
        this.$emit('matched', exactRes[0].ticker);
      }
    },
    async searchExact(query = '') {
      const url = `/api/v1/exact-search-tickers/?search=$${query}`;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            ...this.headers,
            'Content-Type': 'application/json',
          },
        });
        const data = await res.json();
        // Update exact results immediately.
        this.exactResults = data.tickers;
        this.mergeResults();
        return data.tickers;
      } catch (error) {
        console.error('Exact search error: ', error);
      }
    },
    async searchFuzzy(query = '') {
      if (this.fuzzyAbortController) {
        this.fuzzyAbortController.abort();
      }
      this.fuzzyAbortController = new AbortController();
      const url = `/api/v1/tickers/?search=$${query}`;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            ...this.headers,
            'Content-Type': 'application/json',
          },
          signal: this.fuzzyAbortController.signal,
        });
        const data = await res.json();
        // Update fuzzy results when they load.
        this.fuzzyResults = data.tickers;
        this.mergeResults();
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fuzzy search aborted');
        } else {
          console.error('Fuzzy search error: ', error);
        }
      }
    },
    mergeResults() {
      // Merge exact and fuzzy results and ensure exact matches appear first.
      const seen = new Set();
      const combined = [];
      
      // Add exact matches first.
      this.exactResults.forEach((result) => {
        combined.push({ ...result, source: 'exact' });
        seen.add(result.ticker.symbol);
      });
      
      // Append fuzzy results that are not already in exact results.
      this.fuzzyResults.forEach((result) => {
        if (!seen.has(result.ticker.symbol)) {
          combined.push({ ...result, source: 'fuzzy' });
        }
      });
      
      // Optional: sort to keep exact matches on top.
      combined.sort((a, b) => {
        if (a.source === b.source) return 0;
        return a.source === 'exact' ? -1 : 1;
      });
      
      // Update searchResults with a 'display' property used by the autocomplete.
      this.searchResults = combined.map((item) => ({
        ...item,
        display: `${item.ticker.symbol} - ${item.ticker.full_name}`,
      }));
    },
    selectFirstResult(event) {
      // If at least one search result exists, select the first one.
      if (this.searchResults.length > 0) {
        event.preventDefault(); // Prevent default form submission behavior.
        this.handleSelect(this.searchResults[0]);
      }
    },
    handleSelect(option) {
      if (option && option.ticker) {
        this.selected = option.ticker;
        this.$emit('select', option.ticker);
      } else {
        console.error('Ticker not found after selection');
      }
    },
    focusInput() {
      this.$nextTick(() => {
        const inputEl = this.$refs.searchAutocomplete?.$el?.querySelector('input');
        if (inputEl) {
          inputEl.focus();
        }
      });
    },
  },
};
</script>

<style scoped>
/* Add your component styling if needed */
</style>
